import { jsonOr, type DumbStorageSerializer } from "@/utils/dumb-local-storage";
import type { Logger } from "@/utils/logger";
import { ObservableDumbStorageValue } from "@/utils/observable-dumb-local-storage";
import * as z from "zod/v4-mini";

export enum GqlAuthChannelType {
  otp = "otp",
  oauth = "oauth",
}

export enum IntegrationMode {
  Sso = "sso",
  FanfestAuth = "fanfest_auth",
}

export const sdkChannelInfoSchema = z.object({
  channel_id: z.string(),
  app_id: z.string(),
  integration_mode: z.optional(z.string()),
  auth_modal_url: z.optional(z.string()),
});

export type SDKChannelInfoObject = z.infer<typeof sdkChannelInfoSchema>;

export class SDKChannelInfoState {
  #logger: Logger;
  public value: ObservableDumbStorageValue<SDKChannelInfoObject | null>;

  #dumbStorageSerializer: DumbStorageSerializer<SDKChannelInfoObject | null> = {
    read: (v) => {
      const value = jsonOr(v, null);
      if (!value) return null;

      this.#logger.debug("Reading channel info", {
        value,
      });

      const result = sdkChannelInfoSchema.safeParse(value);
      if (!result.success) {
        this.#logger.error("Failed to parse channel info", {
          error: result.error,
        });
        return null;
      }

      this.#logger.debug("Parsed channel info", {
        result,
      });

      return result.data;
    },
    write: (value) => {
      this.#logger.debug("Writing channel info", {
        value,
      });
      if (!value) return null;
      return JSON.stringify(value);
    },
  };

  constructor(logger: Logger) {
    this.#logger = logger.extend(this.constructor.name);
    this.value = new ObservableDumbStorageValue("fanfest-channel-info", null, {
      options: {
        serializer: this.#dumbStorageSerializer,
      },
      storage: localStorage,
    });
  }

  get channelId(): string | null {
    return this.value.get()?.channel_id ?? null;
  }

  get appId(): string | null {
    return this.value.get()?.app_id ?? null;
  }

  get integrationMode(): string | null {
    return this.value.get()?.integration_mode ?? null;
  }

  get authModalUrl(): string | null {
    return this.value.get()?.auth_modal_url ?? null;
  }
}
